import {
  Checkbox,
  CircularProgress,
  Stack,
  SxProps,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material'
import { Fragment, ReactNode, useMemo } from 'react'
import TablePaginationActions from '@/core/components/table/TablePaginationActions.tsx'
import useTableLogic from '@/core/components/table/useTableLogic.ts'
import { TableProps } from '@/core/components/table/types.ts'
import EmptyTable from '@/core/components/table/EmptyTable.tsx'
import Expander from '@/core/components/table/expander/Expander.tsx'

export const fullSpanCell = (
  content: ReactNode,
  columnLength: number,
  sx?: SxProps
) => {
  return (
    <TableRow>
      <TableCell colSpan={columnLength} sx={sx}>
        {content}
      </TableCell>
    </TableRow>
  )
}

const Table = <Row extends {}, K extends keyof Row>({
  columns,
  rows,
  onSelection,
  headerCellStyles,
  cellStyles,
  loading,
  idField,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  isRowSelected: showRowSelected,
  isRowDisabled: showRowDisabled,
  expandable,
  isRowExpandable,
  pagination,
  tableFooter,
  smartPagination = false
}: TableProps<Row, K>) => {
  if (!idField)
    console.error(
      `No se ha establecido un field para usar como ID de la tabla. Esto puede generar comportamientos inesperados.`
    )

  const {
    handleCheckAll,
    allRowsChecked,
    isRowSelected,
    isRowDisabled,
    isRowExpanded,
    handleRowCheck,
    handleRowExpansion,
    rowClicked,
    handleRowClick,
    headerCellStyle,
    cellStyle,
    CELL_PADDING
  } = useTableLogic({
    rows,
    cellStyles,
    headerCellStyles,
    idField,
    onSelection,
    onRowClick,
    loading,
    showRowSelected,
    showRowDisabled
  })

  const totalColumns = useMemo(() => {
    let total = columns.length
    if (onSelection) total += 1
    if (expandable) total += 1
    return total
  }, [columns.length, expandable, onSelection])

  const rowClickedStyle = useMemo(
    () => (row: Row) =>
      onRowClick
        ? {
            backgroundColor: row === rowClicked ? '#cce0f1' : 'transparent',
            '&:hover': { cursor: 'pointer', backgroundColor: '#cce0f1' }
          }
        : undefined,
    [onRowClick, rowClicked]
  )

  return (
    <TableContainer sx={{ backgroundColor: 'white' }}>
      <MuiTable>
        <TableHead>
          <TableRow>
            {!!expandable && rows.length > 0 && (
              <TableCell sx={headerCellStyle} width={40}></TableCell>
            )}
            {!!onSelection && rows.length > 0 && (
              <TableCell sx={headerCellStyle} width={40}>
                <Checkbox onChange={handleCheckAll} checked={allRowsChecked} />
              </TableCell>
            )}
            {columns.map((column, index) => (
              <TableCell key={index} sx={headerCellStyle} width={column.width}>
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            fullSpanCell(
              <Stack justifyContent="center" alignItems="center">
                <CircularProgress />
              </Stack>,
              totalColumns
            )}
          {!loading &&
            rows &&
            rows.length === 0 &&
            fullSpanCell(
              <Stack justifyContent="center" alignItems="center">
                <EmptyTable />
              </Stack>,
              totalColumns
            )}
          {!loading &&
            rows.map((row, index) => {
              if (!row[idField])
                console.error(
                  `El field [${idField as string}] usado como ID de la tabla no existe en algunas filas. Esto puede generar comportamientos inesperados.`
                )
              return (
                <Fragment key={index}>
                  <TableRow
                    onClick={() => handleRowClick(row)}
                    sx={rowClickedStyle(row)}
                  >
                    {!!expandable && (
                      <TableCell sx={cellStyle || { padding: CELL_PADDING }}>
                        {(!isRowExpandable || isRowExpandable(row)) && (
                          <Expander
                            onRowExpansion={(e) => handleRowExpansion(e, row)}
                            checked={isRowExpanded(row)}
                          />
                        )}
                      </TableCell>
                    )}
                    {!!onSelection && (
                      <TableCell sx={cellStyle || { padding: CELL_PADDING }}>
                        <Checkbox
                          onChange={(e) => handleRowCheck(e, row)}
                          checked={showRowSelected?.(row) || isRowSelected(row)}
                          disabled={
                            isRowDisabled(row) || showRowSelected?.(row)
                          }
                        />
                      </TableCell>
                    )}
                    {columns.map((column, index) => {
                      const {
                        field,
                        renderCell
                      }: { field: keyof Row; renderCell: any } = column
                      let cellValue: any

                      if (renderCell) {
                        if (field && field in row)
                          cellValue = renderCell(row[field], row)
                        else cellValue = renderCell(null, row)
                      } else {
                        cellValue = row[field]
                      }
                      return (
                        <TableCell
                          key={index}
                          sx={cellStyle || { padding: CELL_PADDING }}
                          width={column.width}
                        >
                          {cellValue}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                  {expandable &&
                    isRowExpanded(row) &&
                    (!isRowExpandable || isRowExpandable(row)) &&
                    fullSpanCell(
                      expandable(row, index, isRowExpanded(row)),
                      totalColumns
                    )}
                </Fragment>
              )
            })}
        </TableBody>
        <TableFooter>
          {tableFooter?.(totalColumns, cellStyle || { padding: CELL_PADDING })}
          {!!pagination && onPageChange && (
            <TableRow>
              <TablePagination
                rowsPerPageOptions={pagination.rowsPerPageOptions}
                count={pagination.count}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                ActionsComponent={(props) => {
                  return (
                    <>
                      <TablePaginationActions
                        {...props}
                        smart={smartPagination}
                      />
                    </>
                  )
                }}
                labelRowsPerPage={smartPagination ? '' : 'Filas por página:'}
              />
            </TableRow>
          )}
        </TableFooter>
      </MuiTable>
    </TableContainer>
  )
}

export default Table
